import { createContext, useContext, useEffect, useMemo, useState, useCallback, type ReactNode } from "react";
import { doc, onSnapshot } from "firebase/firestore";
import { getFunctions, httpsCallable } from "firebase/functions";
import { db } from "../firebase";
import { formatError } from "../utils/formatError";
import logger from "../utils/logger";
import { EXPERIMENTAL_FEATURES, type ExperimentalFeature as FeatureDef } from "../experimentalFeatures";
import type { User } from "../types";

export interface ExperimentalFeaturesValue {
  /** True only inside the platform test environment (test session or platform admin). */
  active: boolean;
  /** Raw switches as stored; only meaningful when `active`. */
  flags: Record<string, boolean>;
  /** Registered features, for the toggle lists. */
  features: FeatureDef[];
  /** Always false for regular users. */
  isEnabled: (_key: string) => boolean;
  enabledCount: number;
  setFeature: (_key: string, _enabled: boolean) => Promise<void>;
  /** Key currently being saved, for the toggle UI. */
  saving: string | null;
}

const INERT: ExperimentalFeaturesValue = {
  active: false,
  flags: {},
  features: EXPERIMENTAL_FEATURES,
  isEnabled: () => false,
  enabledCount: 0,
  setFeature: async () => { throw new Error("Experimental features can only be changed from the test environment."); },
  saving: null,
};

const ExperimentalFeaturesContext = createContext<ExperimentalFeaturesValue>(INERT);

/**
 * Source of truth for experimental-feature switches. Subscribes to
 * platformSettings/testEnvironment only when the current session is a
 * platform admin or a test session — for everyone else it is inert and
 * performs no reads, so experimental UI can never leak to regular users.
 */
export function useExperimentalFeatures(currentUser: User | null | undefined): ExperimentalFeaturesValue {
  const active = !!currentUser && (currentUser.platformAdmin === true || currentUser.testSession === true);
  const [flags, setFlags] = useState<Record<string, boolean>>({});
  const [saving, setSaving] = useState<string | null>(null);

  useEffect(() => {
    if (!active) return;
    const unsubscribe = onSnapshot(
      doc(db, "platformSettings", "testEnvironment"),
      (snap) => {
        const data = snap.exists() ? snap.data() : null;
        setFlags((data?.experimentalFeatures as Record<string, boolean>) || {});
      },
      (err) => {
        logger.error("Experimental features listener error:", err);
      }
    );
    return () => unsubscribe();
  }, [active]);

  const setFeature = useCallback(async (key: string, enabled: boolean) => {
    setSaving(key);
    try {
      const fn = httpsCallable<{ key: string; enabled: boolean }, { experimentalFeatures: Record<string, boolean> }>(
        getFunctions(), "setExperimentalFeature"
      );
      const result = await fn({ key, enabled });
      setFlags(result.data.experimentalFeatures || {});
    } catch (err) {
      throw new Error(formatError(err));
    } finally {
      setSaving(null);
    }
  }, []);

  return useMemo(() => {
    if (!active) return INERT;
    const isEnabled = (key: string) => flags[key] === true;
    const enabledCount = EXPERIMENTAL_FEATURES.filter((f) => isEnabled(f.key)).length;
    return { active, flags, features: EXPERIMENTAL_FEATURES, isEnabled, enabledCount, setFeature, saving };
  }, [active, flags, setFeature, saving]);
}

export function ExperimentalFeaturesProvider({ value, children }: { value: ExperimentalFeaturesValue; children: ReactNode }) {
  return <ExperimentalFeaturesContext.Provider value={value}>{children}</ExperimentalFeaturesContext.Provider>;
}

/** Read experimental switches anywhere. Outside a provider (tests, public pages) it is inert. */
export function useExperimental(): ExperimentalFeaturesValue {
  return useContext(ExperimentalFeaturesContext);
}

interface ExperimentalFeatureProps {
  feature: string;
  children: ReactNode;
  /** Rendered when the feature is off (default: nothing). */
  fallback?: ReactNode;
}

/** Renders children only while the named experimental feature is switched on. */
export function ExperimentalFeature({ feature, children, fallback = null }: ExperimentalFeatureProps) {
  const { isEnabled } = useExperimental();
  return <>{isEnabled(feature) ? children : fallback}</>;
}
